import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GroupDetailsContent } from "content";
import { Avatar } from "components/Common/Avatar";
import { IconButton } from "components/Interactive/IconButton";
import { Tooltip } from "components/Interactive/Tooltip";
import { ReactComponent as Delete } from "assets/icons/delete.svg";
import { useRole } from "hooks/common/useRole";
import { formatPhoneNumber } from "utils/common";
import { getInitials } from "utils/common/initials";
import { getRowSequenceNumber } from "utils/table";
import { TStudent } from "types/api/student";

type TUseGroupMemberColumns = (options?: {
  onDeleteClick?: (studentId: number) => void;
}) => ColumnDef<TStudent, string>[];

export const useGroupMemberColumns: TUseGroupMemberColumns = (options) => {
  const { onDeleteClick } = options || {};
  const { isUserAdmin } = useRole();

  const columnHelper = createColumnHelper<TStudent>();

  const columns: ColumnDef<TStudent, string>[] = [
    columnHelper.accessor(() => "index", {
      id: "index",
      header: GroupDetailsContent.Table.Header.NUMBER,
      cell: (info) =>
        getRowSequenceNumber(info.row.index, info.table.getState().pagination),
      size: 0.05,
    }),
    columnHelper.accessor(() => "avatar", {
      id: "avatar",
      header: GroupDetailsContent.Table.Header.AVATAR,
      cell: ({ row: { original } }) => (
        <Avatar
          bordered
          size="small"
          image={original?.person.photoUrl}
          initials={
            !original?.person.photoUrl
              ? getInitials(
                  `${original.person.firstName} ${original.person.lastName}`
                )
              : undefined
          }
        />
      ),
      size: 0.1,
    }),
    columnHelper.accessor(() => "name", {
      id: "name",
      header: GroupDetailsContent.Table.Header.NAME,
      cell: (info) => (
        <Tooltip
          mouseEnterDelay={1000}
          text={`${info.row.original.person.firstName} ${info.row.original.person.lastName}`}
          spaceSize="small"
          variant="compact"
        >
          {info.row.original.person.firstName}&nbsp;
          {info.row.original.person.lastName}
        </Tooltip>
      ),
      size: 0.15,
    }),
    columnHelper.accessor((row) => row.person.phone, {
      id: "phone",
      header: GroupDetailsContent.Table.Header.PHONE,
      cell: (info) => (
        <Tooltip
          mouseEnterDelay={1000}
          text={formatPhoneNumber(info.getValue())}
          spaceSize="small"
          variant="compact"
        >
          {formatPhoneNumber(info.getValue())}
        </Tooltip>
      ),
      size: 0.2,
    }),
    columnHelper.accessor(() => "action", {
      id: "action",
      header: GroupDetailsContent.Table.Header.ACTION,
      cell: ({ row }) =>
        isUserAdmin ? (
          <IconButton
            icon={<Delete />}
            onClick={() => onDeleteClick?.(row.original.id)}
          />
        ) : null,
      size: 41,
    }),
  ];

  return columns;
};
